class PromoBar extends HTMLElement {
  constructor() {
    super();
    this.isPaused = sessionStorage.getItem('promoBarPaused') === 'true';
    this.controlBtn = this.querySelector('.promo-bar-control');
    this.marquees = this.querySelectorAll('.marquee');
    this.playIcon = this.querySelector('.promo-bar-play-icon');
    this.pauseIcon = this.querySelector('.promo-bar-pause-icon');
    this.model = this.querySelector('.model-country-selector');
    this.btn = this.querySelector('.country-model-button');
    this.span = this.querySelector('.close');

    // Slider display mode
    this.slider = this.querySelector('.promo-bar-slider');
    this.slidesWrapper = this.querySelector('.promo-bar-slides');
    this.slides = Array.from(this.querySelectorAll('.promo-bar-slide'));
    this.prevArrow = this.querySelector('.promo-bar-slider-arrow--prev');
    this.nextArrow = this.querySelector('.promo-bar-slider-arrow--next');
    this.autoplayDelay = parseInt(this.slider?.dataset.autoplay, 10) || 0;
    this.animate = this.slider?.dataset.animate === 'true';
    this.autoplayTimer = null;
    this.leavingTimer = null;
    this.currentIndex = Math.max(
      0,
      this.slides.findIndex((slide) => slide.classList.contains('is-active'))
    );

    // Without a play/pause button there is no way back out of a paused state
    // that a previous page view (or the marquee mode) left in sessionStorage.
    if (this.slider && !this.controlBtn) this.isPaused = false;

    this.togglePlayPause = this.togglePlayPause.bind(this);
    this.modelOpen = this.modelOpen.bind(this);
    this.modelClose = this.modelClose.bind(this);
    this.onPrevClick = this.onPrevClick.bind(this);
    this.onNextClick = this.onNextClick.bind(this);
    this.suspendAutoplay = this.suspendAutoplay.bind(this);
    this.resumeAutoplay = this.resumeAutoplay.bind(this);
  }

  connectedCallback() {
    // Apply initial paused state if it was previously set in the session
    this.applyPausedState();
    this.controlBtn?.addEventListener('click', this.togglePlayPause);
    this.btn?.addEventListener('click', this.modelOpen);
    this.span?.addEventListener('click', this.modelClose);
    this.model?.addEventListener('keyup', this.onKeyUpEscape);

    this.prevArrow?.addEventListener('click', this.onPrevClick);
    this.nextArrow?.addEventListener('click', this.onNextClick);
    if (this.slider && this.autoplayDelay) {
      this.slider.addEventListener('mouseenter', this.suspendAutoplay);
      this.slider.addEventListener('mouseleave', this.resumeAutoplay);
      this.slider.addEventListener('focusin', this.suspendAutoplay);
      this.slider.addEventListener('focusout', this.resumeAutoplay);
    }
    this.observeLockedHeight();
  }

  disconnectedCallback() {
    this.controlBtn?.removeEventListener('click', this.togglePlayPause);
    this.btn?.removeEventListener('click', this.modelOpen);
    this.span?.removeEventListener('click', this.modelClose);

    this.prevArrow?.removeEventListener('click', this.onPrevClick);
    this.nextArrow?.removeEventListener('click', this.onNextClick);
    this.slider?.removeEventListener('mouseenter', this.suspendAutoplay);
    this.slider?.removeEventListener('mouseleave', this.resumeAutoplay);
    this.slider?.removeEventListener('focusin', this.suspendAutoplay);
    this.slider?.removeEventListener('focusout', this.resumeAutoplay);
    this.stopAutoplay();
    clearTimeout(this.leavingTimer);
    this.unobserveLockedHeight();
  }

  // A locked promo bar is sticky, so the sticky header has to sit below it.
  // Its height depends on font size, padding and wrapping, so it is measured
  // and handed to CSS instead of being hard coded.
  observeLockedHeight() {
    if (this.dataset.locked !== 'true') return;

    const section = this.closest('.shopify-section') || this;
    const setHeight = () => {
      // offsetHeight rounds to whole pixels, so a zoomed 32.8px bar would push
      // the header down to 33px and open a gap. The rect keeps the fraction.
      const height = section.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--promo-bar-height', `${height}px`);
    };

    setHeight();
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(setHeight);
      this.resizeObserver.observe(section);
    } else {
      this.onResize = setHeight;
      window.addEventListener('resize', this.onResize);
    }
  }

  unobserveLockedHeight() {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    if (this.onResize) window.removeEventListener('resize', this.onResize);
    this.onResize = null;
    if (this.dataset.locked === 'true') {
      document.documentElement.style.removeProperty('--promo-bar-height');
    }
  }

  modelOpen() {
    var _self = this;
    this.model.classList.add('modal-open');
    if(_self.querySelector('.modal-content') != null){
      trapFocus(_self.querySelector('.modal-content'), _self.querySelector('.close'));
    }
    document.querySelector('body').classList.add('overflow-hidden');
  }

  onKeyUpEscape(event){
     var _self = this;
    if (event.code.toUpperCase() !== 'ESCAPE') return;
    _self.classList.remove('modal-open');
    if(_self.closest('.localization-wrapper') != null){
      removeTrapFocus(_self.closest('.localization-wrapper').querySelector('.country-model-button'));
    }
    document.querySelector('body').classList.remove('overflow-hidden');
  }

  modelClose() {
    var _self = this;
    this.model.classList.remove('modal-open');
    //this.model.classList.remove('modal-open-footer');
    if(_self.querySelector('.localization-wrapper') != null){
      removeTrapFocus(_self.querySelector('.localization-wrapper .country-model-button'));
    }
    document.querySelector('body').classList.remove('overflow-hidden');
  }

  togglePlayPause() {
    this.isPaused = !this.isPaused;
    sessionStorage.setItem('promoBarPaused', this.isPaused); // Save paused state in sessionStorage
    this.applyPausedState();
  }

  applyPausedState() {
    this.marquees.forEach((marquee) => {
      marquee.style.animationPlayState = this.isPaused ? 'paused' : 'running';
      marquee.setAttribute('aria-live', this.isPaused ? 'off' : 'polite');
    });

    if (this.slider) {
      // While rotating on its own the slider must not spam screen readers;
      // once paused, arrow-driven changes are worth announcing.
      this.slidesWrapper?.setAttribute('aria-live', this.isPaused ? 'polite' : 'off');
      if (this.isPaused) {
        this.stopAutoplay();
      } else {
        this.startAutoplay();
      }
    }

    this.updateButtonIcon();
  }

  updateButtonIcon() {
    this.playIcon?.classList.toggle('hidden', !this.isPaused);
    this.pauseIcon?.classList.toggle('hidden', this.isPaused);
  }

  onPrevClick() {
    this.goTo(this.currentIndex - 1, 'prev');
    this.restartAutoplay();
  }

  onNextClick() {
    this.goTo(this.currentIndex + 1, 'next');
    this.restartAutoplay();
  }

  goTo(index, direction) {
    if (this.slides.length < 2) return;

    const nextIndex = (index + this.slides.length) % this.slides.length;
    if (nextIndex === this.currentIndex) return;

    const current = this.slides[this.currentIndex];
    const next = this.slides[nextIndex];

    current.classList.remove('is-active');
    next.classList.add('is-active');
    this.currentIndex = nextIndex;

    if (this.animate) {
      this.slider.dataset.motion = direction;
      current.classList.add('is-leaving');
      clearTimeout(this.leavingTimer);
      this.leavingTimer = setTimeout(() => current.classList.remove('is-leaving'), 600);
    }
  }

  startAutoplay() {
    if (!this.autoplayDelay || this.slides.length < 2) return;
    this.stopAutoplay();
    this.autoplayTimer = setInterval(() => this.goTo(this.currentIndex + 1, 'next'), this.autoplayDelay);
  }

  stopAutoplay() {
    clearInterval(this.autoplayTimer);
    this.autoplayTimer = null;
  }

  restartAutoplay() {
    if (this.isPaused) return;
    this.startAutoplay();
  }

  suspendAutoplay() {
    this.stopAutoplay();
  }

  resumeAutoplay() {
    if (this.isPaused) return;
    this.startAutoplay();
  }
}

customElements.define('promo-bar', PromoBar);
